import { useEffect, useMemo, useRef } from 'react';
import { Values } from '@lib/interfaces/form';
import {
  ActiveTabState,
  FilterState,
  ModalTableMultiselectStates,
} from './useModalTableMultiselectStates';
import useModalTableMultiselect from './useModalTableMultiselect';
import useModalTableMultiunselect from './useModalTableMultiunselect';
import { ModalTableMultiselectMode, TableTabs } from '../enums';

type SelectedOnlyStates = Pick<
  ModalTableMultiselectStates,
  | 'searchStateSelected'
  | 'paginationStateSelected'
  | 'orderingStateSelected'
  | 'filterStateSelected'
>;

interface UseModalTableModeManagerArgs {
  isOpen: boolean;
  mode: ModalTableMultiselectMode;
  selectedIds: string[];
  filterStateDefault: Values;
  /**
   * Filter field name -> forced value, (re-)applied every time the modal
   * opens — e.g. pin the Sites filter to the Site already chosen on the host
   * form. Pass a value memoized on its own content (not a fresh object
   * literal per render), or this effect re-runs on every render.
   */
  filterOverrides?: Values;
  /** `1` = single-select picker: no Selected tab, always opens on All. */
  selectionLimit?: number;
}

interface UseModalTableModeManagerReturn extends SelectedOnlyStates {
  activeTab: TableTabs;
  activeTabState?: ActiveTabState;
  clearFilter: () => void;
  filterState?: FilterState;
  idsForSelectedTabQuery: string[];
  isSelectedOnlyMode: boolean;
  isSelectedTabActive: boolean;
  orderingState?: ModalTableMultiselectStates['orderingState'];
  paginationState?: ModalTableMultiselectStates['paginationState'];
  searchState?: ModalTableMultiselectStates['searchState'];
  setSelectedIdsForQuery: (ids: string[]) => void;
}

export default function useModalTableModeManager({
  isOpen,
  mode,
  selectedIds,
  filterStateDefault,
  filterOverrides,
  selectionLimit,
}: UseModalTableModeManagerArgs): UseModalTableModeManagerReturn {
  const isSelectedOnlyMode = mode === ModalTableMultiselectMode.SelectedOnly;

  const multiselectHook = useModalTableMultiselect({
    isOpen: !isSelectedOnlyMode && isOpen,
    selected: selectedIds,
    filterStateDefault,
    selectionLimit,
  });

  const multiunselectHook = useModalTableMultiunselect({
    isOpen: isSelectedOnlyMode && isOpen,
    selected: selectedIds,
    filterStateDefault,
  });

  const baseHook = isSelectedOnlyMode ? multiunselectHook : multiselectHook;

  const {
    searchStateSelected,
    paginationStateSelected,
    orderingStateSelected,
    filterStateSelected,
    idsForSelectedTabQuery,
    setSelectedIdsForQuery,
  } = baseHook;

  const activeTabState = !isSelectedOnlyMode
    ? multiselectHook.activeTabState
    : undefined;
  const searchState = !isSelectedOnlyMode
    ? multiselectHook.searchState
    : undefined;
  const paginationState = !isSelectedOnlyMode
    ? multiselectHook.paginationState
    : undefined;
  const orderingState = !isSelectedOnlyMode
    ? multiselectHook.orderingState
    : undefined;
  const filterState = !isSelectedOnlyMode
    ? multiselectHook.filterState
    : undefined;
  const [filterValues, setFilter] = multiselectHook.filterState;
  const [filterValuesSelected, setFilterSelected] =
    baseHook.filterStateSelected;

  // Reset must clear the filter values on BOTH tabs (All + Selected) since
  // the user-facing filter row is shared across them. Without resetting
  // `filterStateSelected` here, the Selected tab keeps stale filter values
  // after the Reset action. `filterStateDefault` already carries any
  // `filterOverrides` folded in by the caller, so a locked/prefilled field
  // resets to its forced value rather than to empty.
  const clearFilter = () => {
    if (setFilter) {
      setFilter({ ...filterStateDefault });
    }
    if (setFilterSelected) {
      setFilterSelected({ ...filterStateDefault });
    }
  };

  // `useModalTableMultiselect` only seeds filterState/filterStateSelected
  // from filterStateDefault on first mount (deliberately, so a user's own
  // filter picks survive close/reopen) — so a locked field must be
  // force-reapplied on every open, or a stale value from an earlier open
  // (e.g. a previously selected Site) lingers.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    const isOpening = !wasOpenRef.current && isOpen;
    wasOpenRef.current = isOpen;
    if (isOpening && filterOverrides && Object.keys(filterOverrides).length) {
      setFilter?.({ ...filterValues, ...filterOverrides });
      setFilterSelected?.({ ...filterValuesSelected, ...filterOverrides });
    }
    // Only the open transition should re-apply overrides — filterValues/
    // filterValuesSelected are read, not depended on, to avoid re-running
    // this on every filter change the user makes while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filterOverrides, setFilter, setFilterSelected]);

  const activeTab = activeTabState ? activeTabState[0] : TableTabs.Selected;

  const isSelectedTabActive = useMemo(() => {
    if (isSelectedOnlyMode) {
      return true;
    }
    return activeTab === TableTabs.Selected;
  }, [activeTab, isSelectedOnlyMode]);

  return {
    activeTab,
    activeTabState,
    clearFilter,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isSelectedOnlyMode,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    setSelectedIdsForQuery,
  };
}
